import AddIcon from '@mui/icons-material/Add';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import {
  type Breakpoint,
  Box,
  Button,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
} from '@mui/material';
import { type ReactNode, useRef, useState } from 'react';

import { useCollapseToIcon } from './use-collapse-to-icon';

export interface AddMenuItem {
  key: string;
  label: string;
  icon?: ReactNode;
  /** A one-line hint under the label. */
  description?: string;
  onClick: () => void;
  disabled?: boolean;
}

export interface AddMenuButtonProps {
  /** Everything that can be added, in menu order. */
  items: AddMenuItem[];
  /** The button's text. Defaults to "Add". */
  label?: string;
  /** Below this breakpoint the button is icon-only. Defaults to `md`. */
  collapseBelow?: Breakpoint;
}

/**
 * A section's one "Add" button. It opens a menu of everything that can be
 * added across the section, so nothing is added by a click that meant "show
 * me what I can add" — the button doesn't guess which one you want. With a
 * single thing to add there's nothing to choose, so it adds it directly.
 * Icon-only below `collapseBelow` where it shares a row with other things;
 * it keeps its label on a row of its own (see `RoomyActions`).
 * @param props - Component props
 * @param props.items - What can be added
 * @param props.label - The button's text
 * @param props.collapseBelow - Breakpoint for the icon-only form
 * @returns The button and its menu
 */
export function AddMenuButton({
  items,
  label = 'Add',
  collapseBelow = 'md',
}: AddMenuButtonProps) {
  const collapsed = useCollapseToIcon(collapseBelow);
  const anchorRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);

  if (items.length === 0) return null;
  const only = items.length === 1 ? items[0] : null;

  return (
    <>
      <Button
        ref={anchorRef}
        size="small"
        variant="contained"
        disableElevation
        disabled={only?.disabled}
        onClick={() => (only ? only.onClick() : setOpen((v) => !v))}
        startIcon={collapsed ? undefined : <AddIcon />}
        endIcon={
          collapsed || only ? undefined : (
            <KeyboardArrowDownIcon
              sx={{
                transition: 'transform 0.15s ease',
                transform: open ? 'rotate(180deg)' : 'none',
              }}
            />
          )
        }
        aria-label={only ? only.label : label}
        aria-haspopup={only ? undefined : 'menu'}
        aria-expanded={open ? 'true' : undefined}
        title={only?.label}
        sx={{
          whiteSpace: 'nowrap',
          ...(collapsed && { minWidth: 0, px: 0.75 }),
        }}
      >
        {collapsed ? <AddIcon fontSize="small" /> : label}
      </Button>
      <Menu
        anchorEl={anchorRef.current}
        open={open}
        onClose={() => setOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            elevation: 3,
            sx: { mt: 0.75, minWidth: 260, borderRadius: 2 },
          },
          list: { sx: { py: 0.5 } },
        }}
      >
        {items.map((item) => (
          <MenuItem
            key={item.key}
            disabled={item.disabled}
            onClick={() => {
              setOpen(false);
              item.onClick();
            }}
            // The icon centres on the whole row — label and hint together —
            // rather than sitting up beside the label alone.
            sx={{ mx: 0.5, borderRadius: 1.5, py: 1, alignItems: 'center' }}
          >
            {item.icon && (
              <ListItemIcon sx={{ color: 'primary.main' }}>
                {item.icon}
              </ListItemIcon>
            )}
            <ListItemText
              primary={item.label}
              secondary={item.description}
              slotProps={{
                primary: { sx: { fontWeight: 600, fontSize: '0.875rem' } },
                secondary: { sx: { fontSize: '0.75rem' } },
              }}
            />
          </MenuItem>
        ))}
        {/* Keeps the last item off the menu's rounded bottom edge. */}
        <Box sx={{ height: 2 }} />
      </Menu>
    </>
  );
}
